/**
 * Ρυθμίσεις ΣΥΣΚΕΥΗΣ του PDA: πόσα τραπέζια/κατηγορίες/προϊόντα ανά σειρά, μέγεθος καρτών, προϊόντα
 * ως κάρτες ή λίστα, κάρτα αναζήτησης (ιδιοκτήτης 7/10/2026: «γρανάζι με settings που αποθηκεύονται
 * τοπικά στη συσκευή» — και «η αναζήτηση σαν setting, default off»).
 *
 * Όπως το θέμα (contexts/ThemeContext): ΑΝΑ ΣΥΣΚΕΥΗ, όχι ανά χρήστη — ο ίδιος σερβιτόρος μπορεί να
 * έχει μικρό κινητό σήμερα και μεγάλο αύριο, και πολλοί σερβιτόροι μοιράζονται PDA. Γι' αυτό
 * localStorage. Τίποτα εδώ δεν αγγίζει ποσά ή payload — μόνο εμφάνιση.
 *
 * Οι κλάσεις Tailwind είναι ΟΛΟΚΛΗΡΕΣ λέξεις σε πίνακες (όχι `grid-cols-${n}`): το Tailwind κρατά στο
 * build μόνο κλάσεις που βρίσκει γραμμένες αυτούσιες στον κώδικα.
 */

import { useSyncExternalStore } from 'react';

export type CardSize = 'sm' | 'md' | 'lg';

export interface WaiterPrefs {
  /** Τραπέζια ανά σειρά στην αρχική (τα 70 του GENERIS σε 2 στήλες = πολύ κύλισμα). */
  tableColumns: 2 | 3 | 4;
  /** Κάρτες κατηγοριών ανά σειρά στο κινητό. */
  categoryColumns: 3 | 4 | 5;
  categorySize: CardSize;
  productView: 'cards' | 'list';
  /** Μόνο για κάρτες. */
  productColumns: 1 | 2 | 3;
  productSize: CardSize;
  /** Κάρτα «Αναζήτηση» πρώτη στον πίνακα κατηγοριών — κλειστή εκτός αν τη θέλει η συσκευή. */
  showSearch: boolean;
}

export const DEFAULT_PREFS: WaiterPrefs = {
  tableColumns: 2,
  categoryColumns: 4,
  categorySize: 'sm',
  productView: 'cards',
  productColumns: 2,
  productSize: 'md',
  showSearch: false,
};

const STORAGE_KEY = 'xentrix_waiter_prefs';

const ALLOWED: { [K in keyof WaiterPrefs]: ReadonlyArray<WaiterPrefs[K]> } = {
  tableColumns: [2, 3, 4],
  categoryColumns: [3, 4, 5],
  categorySize: ['sm', 'md', 'lg'],
  productView: ['cards', 'list'],
  productColumns: [1, 2, 3],
  productSize: ['sm', 'md', 'lg'],
  showSearch: [false, true],
};

/** Ό,τι δεν είναι έγκυρη τιμή (παλιά έκδοση, χειροκίνητο σκάλισμα) → προεπιλογή, ανά πεδίο. */
export function sanitizePrefs(raw: unknown): WaiterPrefs {
  const src = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const out = { ...DEFAULT_PREFS } as Record<keyof WaiterPrefs, unknown>;
  (Object.keys(ALLOWED) as Array<keyof WaiterPrefs>).forEach((key) => {
    if ((ALLOWED[key] as ReadonlyArray<unknown>).includes(src[key])) out[key] = src[key];
  });
  return out as unknown as WaiterPrefs;
}

const read = (): WaiterPrefs => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? sanitizePrefs(JSON.parse(stored)) : { ...DEFAULT_PREFS };
  } catch {
    // private mode / αποκλεισμένο storage / χαλασμένο JSON — οι προεπιλογές αρκούν
    return { ...DEFAULT_PREFS };
  }
};

let current: WaiterPrefs = read();
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((listener) => listener());

export function getPrefs(): WaiterPrefs {
  return current;
}

/** Αλλαγή μίας ή περισσότερων ρυθμίσεων — ισχύει αμέσως σε όλες τις οθόνες του app. */
export function setPrefs(patch: Partial<WaiterPrefs>): void {
  current = sanitizePrefs({ ...current, ...patch });
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
  } catch {
    /* ισχύει για όσο μένει ανοιχτή η σελίδα */
  }
  notify();
}

export function resetPrefs(): void {
  current = { ...DEFAULT_PREFS };
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ό.π. */
  }
  notify();
}

/** Για tests: ξαναδιάβασε από το storage. */
export function reloadPrefs(): void {
  current = read();
  notify();
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export function useWaiterPrefs(): WaiterPrefs {
  return useSyncExternalStore(subscribe, getPrefs, getPrefs);
}

/* ─────────────── κλάσεις ανά ρύθμιση (ολόκληρες, για το Tailwind) ─────────────── */

export const TABLE_GRID: Record<WaiterPrefs['tableColumns'], string> = {
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
};

/** Κάρτα τραπεζιού ανά πλήθος στηλών: όσο στενεύει, ο αριθμός πάνω και το ποσό από κάτω. */
export const TABLE_CARD: Record<WaiterPrefs['tableColumns'], {
  minH: string; pad: string; number: string; total: string; stacked: boolean; zone: boolean;
}> = {
  2: { minH: 'min-h-[96px]', pad: 'p-3', number: 'text-3xl', total: 'text-lg', stacked: false, zone: true },
  3: { minH: 'min-h-[80px]', pad: 'p-2.5', number: 'text-2xl', total: 'text-sm', stacked: true, zone: true },
  4: { minH: 'min-h-[64px]', pad: 'p-2', number: 'text-xl', total: 'text-xs', stacked: true, zone: false },
};

/** 4 στήλες = η προεπιλογή του πίνακα κατηγοριών (7/10/2026)· στα πλατιά, περισσότερες. */
export const CATEGORY_GRID: Record<WaiterPrefs['categoryColumns'], string> = {
  3: 'grid-cols-3 sm:grid-cols-5',
  4: 'grid-cols-4 min-[480px]:grid-cols-5 sm:grid-cols-6',
  5: 'grid-cols-5 sm:grid-cols-7',
};

/** Ύψος κάρτας κατηγορίας και ΜΕΓΙΣΤΟ μέγεθος γραμμάτων (το fitCategoryFont μικραίνει ώσπου να χωρά). */
export const CATEGORY_CARD: Record<CardSize, { minH: string; maxFont: number }> = {
  sm: { minH: 'min-h-[44px]', maxFont: 12 },
  md: { minH: 'min-h-[54px]', maxFont: 13 },
  lg: { minH: 'min-h-[66px]', maxFont: 15 },
};

/** 2 στήλες = η προεπιλογή (μεγάλα ελληνικά ονόματα· βλ. ui.test.tsx). */
export const PRODUCT_GRID: Record<WaiterPrefs['productColumns'], string> = {
  1: 'grid-cols-1 sm:grid-cols-2',
  2: 'grid-cols-2 sm:grid-cols-3',
  3: 'grid-cols-3 sm:grid-cols-4',
};

export const PRODUCT_CARD: Record<CardSize, { minH: string; name: string; price: string }> = {
  sm: { minH: 'min-h-[60px]', name: 'text-[13px]', price: 'text-sm' },
  md: { minH: 'min-h-[76px]', name: 'text-[15px]', price: 'text-base' },
  lg: { minH: 'min-h-[96px]', name: 'text-[17px]', price: 'text-lg' },
};

export const PRODUCT_ROW: Record<CardSize, { minH: string; name: string; price: string }> = {
  sm: { minH: 'min-h-[44px]', name: 'text-[14px]', price: 'text-sm' },
  md: { minH: 'min-h-[52px]', name: 'text-[15px]', price: 'text-base' },
  lg: { minH: 'min-h-[60px]', name: 'text-[17px]', price: 'text-lg' },
};
